import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, from, map, Observable, skip, tap, throwError } from 'rxjs';
import { BaseService } from '../../core/services/base.service';
import { SharedDataService } from '../../shared/shared-data.service';
import { ToasterService } from '../../shared/toaster.service';
import { StorageService, SessionStorageKey } from '../../core/services/storage.service';
import { AppointmentAcceptanceOptionEnum } from '../../shared/appointment-acceptance-constants';
import { ReceiptMethod, SetReceiptMethodResponse } from '../../shared/receipt-method-constants';
declare const UniversalAPIBridge: any;

export interface CommercialDigitalImgDto {
  ucr: string;
  cra: string;
  companyName: string;
  governorate: string;
  station: string;
  registrationDate: string;
  officeName: string;
  digitalImageUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class FoundationServiceService extends BaseService {


  private readonly apiUrl = environment.apiUrl
  constructor(
    protected override http: HttpClient,
    private sharedData: SharedDataService,
    private toaster: ToasterService,
    private storage: StorageService
  ) {
    super(http);
  }
  /**
   * Get lookup data for specific type
   * @param type - type of lookup (ex: "LAW")
   */
  getLookupByType(type: string): Observable<any> {

    return this.get<any>(`${this.apiUrl}/gafi-cpcr/lookup` + `?setType=${type}`);
  }
  getLookupEnums(key: string): Observable<any> {
    const params = new HttpParams().set('type', key);
    return this.get<any>(`${this.apiUrl}/gafi-cpcr/lookup/enums`, params);
  }
  getActivitySectors(law: number, legal: number): Observable<any> {
    return this.get<any>(`${this.apiUrl}/gafi-cpcr/lookup/activity-hierarchy/sectors?establishedLawId=${law}&legalSituationId=${legal}`);
  }
  getActivitySubSectors(sectorId: number, law: number, legal: number): Observable<any> {
    return this.get<any>(`${this.apiUrl}/gafi-cpcr/lookup/activity-hierarchy/sub-sectors?establishedLawId=${law}&legalSituationId=${legal}&sectorId=${sectorId}`);
  }
  getActivities(subsectorId: number, law: number, legal: number): Observable<any> {
    return this.get<any>(`${this.apiUrl}/gafi-cpcr/lookup/activity-hierarchy/activities?establishedLawId=${law}&legalSituationId=${legal}&subSectorId=${subsectorId}`);
  }
  getCountries(page: number = 0, size: number = 1000): Observable<any> {
    return this.get<any>(`${this.apiUrl}/gafi-cpcr/lookup/country/get-all?page=${page}&size=${size}`);
  }


  getLookupTypeByParentId(lookupType: string, parentID: number): Observable<any> {
    return this.get<any>(`${this.apiUrl}/gafi-cpcr/lookup/${lookupType}/${parentID}`);
  }


  getRequestByType(type: string, page: number = 0, size: number = 10, sort: string = 'createdDate,desc'): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort);

    return this.http.get<any>(`${this.apiUrl}/gafi-cpcr/establishment/workflow/${type}`, { params });
  }


  createRequest(body: any): Observable<any> {
    return this.post<any>(`${this.apiUrl}/gafi-cpcr/establishment/create`, body);
  }

  canProgressServices(): Observable<any> {
    return this.get(`${this.apiUrl}/gafi-cpcr/system/settings/website/can-create`);
  }
  getVipChamberOfCommerceServiceFeeEnabled(): Observable<boolean> {
    return this.get<{ data: { enableVipChamberOfCommerceServiceFee: boolean } }>(
      this.apiUrl + '/gafi-cpcr/system/settings/website'
    ).pipe(map(response => {
      const enabled = response.data?.enableVipChamberOfCommerceServiceFee;
      if (typeof enabled !== 'boolean') {
        throw new Error('Unable to load VIP fee settings');
      }
      return enabled;
    }));
  }

  canGoToFreeZone(): Observable<any> {
    return this.get(`${this.apiUrl}/gafi-cpcr/system/settings/website/enable-free-zone`);
  }

  startService(): Observable<any> {
    const state = this.sharedData.getFoundationState();

    if (!state.lawType || !state.legalForm || !state.establishmentTypeId ||
      (state.isLaw72 && !state.investmentNatureId) ||
      (state.isFreeZoneNatureSelected && !state.freeZoneTypeId) ||
      !state.acceptTerms) {
      this.toaster.error('من فضلك تأكد من إدخال جميع البيانات المطلوبة ⚠️');
      return throwError(() => new Error('Validation failed'));
    }
    const vip = this.storage.getSession(SessionStorageKey.IsVip);
    const requestPayload = {
      establishedLawId: state.lawType,
      legalSituationId: state.legalForm,
      establishmentTypeId: state.establishmentTypeId,
      investmentNatureId: state.investmentNatureId || null,
      freeZoneTypeId: state.isFreeZoneNatureSelected ? state.freeZoneTypeId : null,
      vip: vip,

    };

    return this.createRequest(requestPayload).pipe(
      tap((res: any) => {
        const requestId = res?.data?.id;
        this.sharedData.setRequestId(requestId);
      }),
      catchError((err) => {
        const errorMessage =
          err?.error?.message ||
          err?.error?.error?.message ||
          err?.message ||
          'حدث خطأ اثناء بدء الخدمه';

        this.toaster.error(errorMessage);
        return throwError(() => err);
      })
    );
  }
  updateRequestStatus(requestId: number, wantedStatus?: string, note?: string, primaryActivityId?: number): Observable<any> {
    return this.post<any>(`${this.apiUrl}/gafi-cpcr/workflow/next-step`, { requestId, wantedStatus, note, primaryActivityId });
  }
  /**
   * تسجيل طريقة استلام مستندات الشركة للطلب.
   * يستخدم POST لمطابقة باقي نقاط النهاية المشابهة.
   * TODO: confirm HTTP verb
   */
  setReceiptMethod(requestId: number, receiptMethod: ReceiptMethod): Observable<SetReceiptMethodResponse> {
    return this.http.put<SetReceiptMethodResponse>(
      `${this.apiUrl}/gafi-cpcr/establishment/${requestId}/receipt-method`,
      { receiptMethod }
    );
  }
  preEstablishmentData(requestId: number, body: any): Observable<any> {
    return this.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/data-before`, body);
  }
  companyData(requestId: number, body: any): Observable<any> {
    return this.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/company-data`, body);
  }
  activityData(requestId: number, body: any): Observable<any> {
    return this.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/activities`, body);
  }
  deleteActivity(requestId: number, activityId: number): Observable<any> {
    return this.delete<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/activities/${activityId}`);
  }
  capitalData(requestId: number, body: any): Observable<any> {
    return this.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/capital-data`, body);
  }

  createShareholder(requestId: number, body: any): Observable<any> {
    return this.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/shareholders`, body);
  }

  deleteShareholder(requestId: number, shareholderId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/shareholders/${shareholderId}`);
  }

  createAddressData(requestId: number, body: any) {
    return this.http.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/address-data`, body);
  }

  createSignatureData(requestId: number, body: any) {
    return this.http.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/agents`, body);
  }

  deleteSignatureData(requestId: number, agentId: number) {
    return this.http.delete<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/agents/${agentId}`);
  }

  updateSignatureSettings(requestId: number, payload: { contractSignatureCount: number }): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/signature-settings`, payload);
  }

  createPoa(requestId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/poa`, payload);
  }

  deletePoa(requestId: number, poaId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/poa/${poaId}`);
  }

  createAuditorData(requestId: number, body: any): Observable<any> {
    return this.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/auditor-data`, body);
  }

  createLegalAdvisorData(requestId: number, body: any): Observable<any> {
    return this.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/legal-advisor`, body);
  }

  createSupervisoryBoardMember(requestId: number, body: any): Observable<any> {
    return this.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/supervisory-board`, body);
  }

  deleteSupervisoryBoardMember(requestId: number, memberId: number): Observable<void> {
    return this.delete<void>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/supervisory-board/${memberId}`);
  }

  createStatuteData(requestId: number, body: any): Observable<any> {
    return this.put<any>(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/statute-data`, body);
  }

  createTemporaryLabor(requestId: number, data: any[]): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/temporary-labor`, data);
  }

  getDraftOfTab(requestId: number, type: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/section/${type}`);
  }

  // Placeholder for saving reference person data (adjust endpoint and payload as needed)
  createReferencePersonData(requestId: number, data: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/refer-persons`, data);
  }

  createCompanyData(requestId: number, data: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/company-data`, data);
  }

  createInvestorData(requestId: number, data: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/investor-data`, data);
  }

  createFounderData(requestId: number, data: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/founder-data`, data);
  }

  createPartner(requestId: number, partnerData: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/partners`, partnerData);
  }

  deletePartner(requestId: number, partnerId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/partners/${partnerId}`);
  }

  createManager(requestId: number, managerData: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/managers`, managerData);
  }

  deleteManager(requestId: number, managerId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/managers/${managerId}`);
  }
  setTabValidation(data: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/validation`, data);
  }
  getTabValidation(requestId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/validation/${requestId}`);
  }

  getNotes(requestId: number, screenSection: string, status: string): Observable<any> {
    const params = new HttpParams()
      .set('section', screenSection)
      .set('status', status);
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/requests/${requestId}/notes`, { params });
  }

  payRequest(_requestId: number | null): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/gafi-cpcr/efinance/card?redirectUrl=${window.location.href}`,
      {
        requestId: _requestId,
        type: 'VIP',
        billing: {
          email: "",
          firstName: "",
          lastName: "",
          phone: ""
        }
      },
      { responseType: 'text' });
  }

  getRequestPaidAndVipData(requestId: number | null) {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}`);
  }

  fetchAttachments(requestNumber: any): Observable<any> {
    return this.get(`${this.apiUrl}/gafi-cpcr/actions/by-number/${requestNumber}`);
  }

  getRequestById(requestId: number): Observable<any> {
    return this.get(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}`);
  }

  checkCompanyNameExists(name: string, lang: string = 'ar', excludeRequestId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/company-name/check`, {
      params: { name, lang, excludeRequestId }
    });
  }

  getDraftOfTabOfExternalParties(requestId: number, type: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/section/${type}`);
  }
  private waitForBridge(timeout = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      const interval = 100;
      let elapsed = 0;
      const timer = setInterval(() => {
        if (typeof UniversalAPIBridge !== 'undefined') {
          clearInterval(timer);
          resolve();
        } else if ((elapsed += interval) >= timeout) {
          clearInterval(timer);
          reject(new Error('UniversalAPIBridge extension not detected within timeout.'));
        }
      }, interval);
    });
  }
  getSignatureFields(fileBlob: Blob): Observable<any> {
    const formData = new FormData();
    formData.append('file', fileBlob, 'document.pdf');
    return this.http.post('http://localhost:9023/signature/signature-fields', formData);
  }
  checkSignatureAppAvailability(): Observable<any> {
    return this.http.get(`http://localhost:9023/signature/health`);
  }

  updateAttachments(requestId: number, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/documents-data`, payload);
  }
  signDocument(payload: any): Observable<Blob> {
    const formData = new FormData();
    formData.append('file', payload.file, 'document.pdf');
    formData.append('pin', payload.pin);
    formData.append('fieldName', payload.fieldName);
    return this.http.post(`http://localhost:9023/signature/sign`, formData, {
      responseType: 'blob' // ✅ expect PDF file
    });
  }
  initiatePayment(payRequestEfinance: PayRequestEfinance): Observable<any> {
    return this.http.post(`${this.apiUrl}/gafi-cpcr/efinance/initiate/vip?redirectUrl=${window.location.href}`, payRequestEfinance);
  }

  initiatePaymentFees(payRequestEfinance: PayRequestEfinance): Observable<any> {
    return this.http.post(`${this.apiUrl}/gafi-cpcr/efinance/initiate/fees?redirectUrl=${window.location.href}`, payRequestEfinance);
  }

  getAllowedStatuses(): Observable<any> {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/request-search/allowed-statuses`);
  }

  searchRequests(body: any, page: number = 0, size: number = 10, sort: string = 'createdDate,desc'): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort);
    return this.http.post(`${this.apiUrl}/gafi-cpcr/establishment/request-search`, body, { params });
  }

  deleteRequest(requestId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}`);
  }

  payCash(requestId: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/source/hybrid`, null);
  }
  getCurrencyRates(): Observable<any> {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/lookup/currency-rate/daily`);
  }

  createTransactionAuthority(payload: any) {
    return this.http.post<any>(
      `${this.apiUrl}/gafi-cpcr/transaction-authority/create`,
      payload
    );
  }
  searchCompanies(name: string) {
    return this.http.get<any>(`${this.apiUrl}/gafi-cpcr/establishment/company-data/search`, { params: { name } });
  }


  getNonConfusionCertificateData(certificateNumber: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/gg-integration/non-confusion-certificate/${certificateNumber}`);
  }

  nonConfusionCertificateIntegration(): Observable<any> {
    return this.get(`${this.apiUrl}/gafi-cpcr/system/settings/website/non-confusion-certificate-gg-integration`);
  }
  updateNotaryStageOne(requestId: number, data: any): Observable<any> {
    return this.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/notary-stage-one/documentation-record-document-url`, data);
  }
  cancelRequest(requestId: number): Observable<any> {
    return this.patch(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/cancel`, null);
  }
  evaluateFees(payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/gafi-cpcr/establishment/rules/evaluate`, payload);
  }

  searchLookups(setType: string, query?: string): Observable<any> {
    let params = new HttpParams().set('setType', setType);
    if (query) {
      params = params.set('query', query);
    }

    return this.http.get(`${this.apiUrl}/gafi-cpcr/lookup/search`, { params });
  }
  getCommericalDigitalImg(CRN: string): Observable<CommercialDigitalImgDto> {
    return this.http.get<CommercialDigitalImgDto>(`${this.apiUrl}/gafi-cpcr/gg-integration/cra/digital-image/${CRN}`);
  }
  checkCommercialRegAvailability(): Observable<any> {
    return this.get(`${this.apiUrl}/gafi-cpcr/system/settings/website/digital-cra-image-gg-integration`);
  }
  putOtherDocument(requestId: number, payload: OtherDocumentItem): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}/gafi-cpcr/establishment/${requestId}/other-documents`,
      payload
    );
  }
  getOtherDocuments(requestId: number) {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/requests/${requestId}/other-documents`);
  }
  getCommercialRegistryRegistrationDocument(requestId: number) {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/commercial-registry/registration-document-url`);
  }
  updateCommercialRegistryRegistrationDocument(requestId: number, data: any) {
    return this.http.put(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/commercial-registry/registration-document-url`, data);
  }
  iScoreIntegrationEnabled(): Observable<any> {
    return this.get(`${this.apiUrl}/gafi-cpcr/system/settings/website/iscore-gg-integration`);
  }

  /** Fetch bank certificate data from iScore using certificateNumber, certificateDate, idDocument */
  getIScoreCertificateData(payload: {
    certificateNumber: string;
    bankId: string;
    requestId: number;
  }): Observable<any> {
    const encodedCertNo = encodeURIComponent(payload.certificateNumber);
    const params = new HttpParams()
      .set('bankId', payload.bankId)
      .set('requestId', payload.requestId.toString());
    return this.get<any>(
      `${this.apiUrl}/gafi-cpcr/gg-integration/iscore-bank-certificate/${encodedCertNo}`,
      params
    );
  }
  // /establishment/{requestId}/additional-files-url

  getindustrialChambertwo(requestId: number) {
    return this.http.get(`${this.apiUrl}/gafi-cpcr/establishment/${requestId}/additional-files-url`);
  }
  updateManagerAcceptanceDeclarationUrl(requestId: number, body: { type: string; id: number; url: any }) {
    return this.http.put(`${environment.apiUrl}/gafi-cpcr/establishment/${requestId}/manager-acceptance-declarations/url`, body);
  }

  downloadAppointmentAcceptanceDeclarationForm(acceptanceType: string): void {
    const fileNames: Record<string, string> = {
      [AppointmentAcceptanceOptionEnum.MANUAL_ATTACHMENT]: 'اقرار قبول تعيين  مدير - عضو (1).pdf',
      [AppointmentAcceptanceOptionEnum.POWER_OF_ATTORNEY]: 'صيغة توكيل عام تاسيس  شركات وتعيين المدير- عضو مجلس الادارة.pdf'
    };
    const fileName = fileNames[acceptanceType];

    if (!fileName) {
      return;
    }

    const link = document.createElement('a');
    link.href = `assets/${encodeURI(fileName)}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }




}

export interface SettlementAmount {
  SettlementAccountCode: string
  SettlementAmountsDescription: string
  amount: number | string;
}
export interface SettlementAmountsWrapper {
  SettlementAmounts: SettlementAmount[];
}
export interface PayRequestEfinance {
  settlementAmountsWrapper?: SettlementAmountsWrapper,
  serviceCode: string,
  requestId: number | null
}
interface OtherDocumentItem {
  id?: number;
  establishmentRequestId?: number;
  description?: string;
  url?: string;
}
